import BigNumber from 'bignumber.js';
import _typeof from '@babel/runtime/helpers/typeof';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import NaCl from 'tweetnacl';
import CryptoJS from 'crypto-js';

function byteToHex(byteArray) {
  // parseInt is needed for eliminating invalid number of arguments warning
  // for toString function
  return byteArray.reduce(function (output, elem) {
    return output + "0".concat(parseInt(elem, 10).toString(16)).slice(-2);
  }, '');
}
function hexToByte(str) {
  if (!str) {
    return new Uint8Array(0);
  }

  var a = [];

  for (var i = 0, len = str.length; i < len; i += 2) {
    a.push(parseInt(str.substr(i, 2), 16));
  }

  return new Uint8Array(a);
}
function stringToHex(str) {
  var result = [];
  var hex;

  for (var i = 0; i < str.length; i++) {
    hex = Number(str.charCodeAt(i)).toString(16).padStart(2, '0');
    result.push(hex);
  }

  return result.join('');
}
function sanitizeHex(str) {
  return str.replace(/^0x/, '').toUpperCase();
}
/**
 * Changes hex string from little-endian to big-endian.
 *
 * @param {string} data
 * @returns {string | *}
 */

function fixByteOrder(data) {
  // match - splits string to array of 2 characters
  // reverse - changes order of chunks
  // join - combines chunks to string
  return data.match(/.{1,2}/g).reverse().join('');
}

/* eslint-disable no-proto */
function TransactionDataError(message) {
  this.constructor.prototype.__proto__ = Error.prototype;

  if (Error.captureStackTrace) {
    Error.captureStackTrace(this, this.constructor);
  }

  this.name = this.constructor.name;
  this.message = message;
}

/**
 * Response field names
 */
var TX_FIELDS = {
  /** address */
  ADDRESS: 'address',

  /** transaction amount */
  AMOUNT: 'amount',

  /** block id */
  BLOCK_ID: 'blockId',

  /** block id */
  BLOCK_ID_FROM: 'blockIdFrom',

  /** block id */
  BLOCK_ID_TO: 'blockIdTo',

  /** message */
  MSG: 'message',

  /** message length */
  MSG_LEN: 'messageLength',

  /** number of sender transactions */
  MESSAGE_ID: 'messageId',

  /** node */
  NODE_ID: 'nodeId',

  /** number of node message */
  NODE_MESSAGE_ID: 'nodeMessageId',

  /** public key */
  PUBLIC_KEY: 'publicKey',

  /** sender address */
  SENDER: 'sender',

  /** account */
  STATUS_ACCOUNT: 'accountStatus',

  /** node status */
  STATUS_NODE: 'nodeStatus',

  /** transaction time */
  TIME: 'time',

  /** transaction id */
  TRANSACTION_ID: 'transactionId',

  /** transaction type */
  TYPE: 'type',

  /** vip hash */
  VIP_HASH: 'vipHash',

  /** number of wires */
  WIRE_COUNT: 'wireCount',

  /** wires */
  WIRES: 'wires'
};
/**
 * Transaction types
 */

var TX_TYPES = {
  BROADCAST: 'broadcast',
  CHANGE_ACCOUNT_KEY: 'change_account_key',
  CHANGE_NODE_KEY: 'change_node_key',
  CREATE_ACCOUNT: 'create_account',
  CREATE_NODE: 'create_node',
  LOG_ACCOUNT: 'log_account',
  GET_ACCOUNT: 'get_account',
  GET_ACCOUNTS: 'get_accounts',
  FIND_ACCOUNTS: 'find_accounts',
  GET_BLOCK: 'get_block',
  GET_BLOCKS: 'get_blocks',
  GET_BROADCAST: 'get_broadcast',
  GET_FIELDS: 'get_fields',
  GET_LOG: 'get_log',
  GET_MESSAGE: 'get_message',
  GET_MESSAGE_LIST: 'get_message_list',
  GET_SIGNATURES: 'get_signatures',
  GET_TRANSACTION: 'get_transaction',
  GET_VIPKEYS: 'get_vipkeys',
  RETRIEVE_FUNDS: 'retrieve_funds',
  SEND_AGAIN: 'send_again',
  SEND_MANY: 'send_many',
  SEND_ONE: 'send_one',
  SET_ACCOUNT_STATUS: 'set_account_status',
  SET_NODE_STATUS: 'set_node_status',
  UNSET_ACCOUNT_STATUS: 'unset_account_status',
  UNSET_NODE_STATUS: 'unset_node_status'
};
/**
 * Transaction types map
 */

var TX_TYPES_MAP = {
  '03': TX_TYPES.BROADCAST,
  '04': TX_TYPES.SEND_ONE,
  '05': TX_TYPES.SEND_MANY,
  '06': TX_TYPES.CREATE_ACCOUNT,
  '07': TX_TYPES.CREATE_NODE,
  '08': TX_TYPES.RETRIEVE_FUNDS,
  '09': TX_TYPES.CHANGE_ACCOUNT_KEY,
  '0A': TX_TYPES.CHANGE_NODE_KEY,
  '0B': TX_TYPES.SET_ACCOUNT_STATUS,
  '0C': TX_TYPES.SET_NODE_STATUS,
  '0D': TX_TYPES.UNSET_ACCOUNT_STATUS,
  '0E': TX_TYPES.UNSET_NODE_STATUS,
  '0F': TX_TYPES.LOG_ACCOUNT,
  10: TX_TYPES.GET_ACCOUNT,
  11: TX_TYPES.GET_LOG,
  12: TX_TYPES.GET_BROADCAST,
  13: TX_TYPES.GET_BLOCKS,
  14: TX_TYPES.GET_TRANSACTION,
  15: TX_TYPES.GET_VIPKEYS,
  16: TX_TYPES.GET_SIGNATURES,
  17: TX_TYPES.GET_BLOCK,
  18: TX_TYPES.GET_ACCOUNTS,
  19: TX_TYPES.GET_MESSAGE_LIST,
  '1A': TX_TYPES.GET_MESSAGE,
  '1B': TX_TYPES.GET_FIELDS
};
/**
 * Contract ABI
 */

var ABI = [{
  inputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'constructor'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'owner',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'value',
    type: 'uint256'
  }],
  name: 'Approval',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'MinterAdded',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'minter',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'MinterApproval',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'MinterRemoved',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'OwnerAdded',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'OwnerRemoved',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'Paused',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'PauserAdded',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'PauserRemoved',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'value',
    type: 'uint256'
  }],
  name: 'Transfer',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'Unpaused',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'uint64',
    name: 'to',
    type: 'uint64'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint128',
    name: 'message',
    type: 'uint128'
  }],
  name: 'Unwrap',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'uint64',
    name: 'from',
    type: 'uint64'
  }, {
    indexed: false,
    internalType: 'uint64',
    name: 'txid',
    type: 'uint64'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'Wrap',
  type: 'event'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'addMinter',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'addOwner',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'addPauser',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'address',
    name: 'owner',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }],
  name: 'allowance',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'value',
    type: 'uint256'
  }],
  name: 'approve',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'balanceOf',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'decimals',
  outputs: [{
    internalType: 'uint8',
    name: '',
    type: 'uint8'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'subtractedValue',
    type: 'uint256'
  }],
  name: 'decreaseAllowance',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'minter',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'subtractedValue',
    type: 'uint256'
  }],
  name: 'decreaseMinterAllowance',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'addedValue',
    type: 'uint256'
  }],
  name: 'increaseAllowance',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'minter',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'addedValue',
    type: 'uint256'
  }],
  name: 'increaseMinterAllowance',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'isMinter',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'isOwner',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'isPauser',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'address',
    name: 'minter',
    type: 'address'
  }],
  name: 'minterAllowance',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'minter',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'minterApprove',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'name',
  outputs: [{
    internalType: 'string',
    name: '',
    type: 'string'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [],
  name: 'pause',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'paused',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [],
  name: 'reclaimEther',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'contract IERC20',
    name: '_token',
    type: 'address'
  }],
  name: 'reclaimToken',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'removeMinter',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'removePauser',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [],
  name: 'renounceMinter',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [],
  name: 'renounceOwner',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [],
  name: 'renouncePauser',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'symbol',
  outputs: [{
    internalType: 'string',
    name: '',
    type: 'string'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'totalSupply',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'value',
    type: 'uint256'
  }],
  name: 'transfer',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'value',
    type: 'uint256'
  }],
  name: 'transferFrom',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [],
  name: 'unpause',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'uint64',
    name: 'to',
    type: 'uint64'
  }],
  name: 'unwrap',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'uint64',
    name: 'to',
    type: 'uint64'
  }, {
    internalType: 'uint128',
    name: 'message',
    type: 'uint128'
  }],
  name: 'unwrapFrom',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'uint64',
    name: 'to',
    type: 'uint64'
  }, {
    internalType: 'uint128',
    name: 'message',
    type: 'uint128'
  }],
  name: 'unwrapMessage',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'uint64',
    name: 'from',
    type: 'uint64'
  }, {
    internalType: 'uint64',
    name: 'txid',
    type: 'uint64'
  }],
  name: 'wrapTo',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}];

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it["return"] != null) it["return"](); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
function crc16(data) {
  var d = hexToByte(sanitizeHex(data));
  var crc = 0x1d0f;

  var _iterator = _createForOfIteratorHelper(d),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var b = _step.value;
      var x = crc >> 8 ^ b;
      x ^= x >> 4;
      crc = (crc << 8 ^ x << 12 ^ x << 5 ^ x) & 0xFFFF;
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  var result = "0000".concat(crc.toString(16));
  return result.substr(result.length - 4);
}
/**
 * Calculates a ADS account address checksum.
 *
 * @param nodeId the node id (4 hexadecimal characters)
 * @param userAccountId the id of the user account (8 hexadecimal characters)
 * @returns {string} the account address checksum (4 hexadecimal characters)
 */

function addressChecksum(nodeId, userAccountId) {
  return sanitizeHex(crc16("".concat(sanitizeHex(nodeId)).concat(sanitizeHex(userAccountId))));
}
/**
 * Formats ADS account address.
 *
 * @param nodeId node identifier
 * @param userAccountId user account identifier
 * @returns {string} formatted address
 */

function formatAddress(nodeId, userAccountId) {
  return "".concat(sanitizeHex(nodeId), "-").concat(sanitizeHex(userAccountId), "-").concat(addressChecksum(nodeId, userAccountId));
}
/**
 * Splits ADS account address into parts.
 *
 * @param address ADS account address
 * @returns {{nodeId: string, userAccountId: string, checksum: string}|null}
 */

function splitAddress(address) {
  var addressRegexp = /^([0-9a-fA-F]{4})-([0-9a-fA-F]{8})-([0-9a-fA-F]{4}|XXXX)$/;
  var matches = addressRegexp.exec(address);

  if (!matches) {
    return null;
  }

  return {
    nodeId: sanitizeHex(matches[1]),
    userAccountId: sanitizeHex(matches[2]),
    checksum: matches[3] === 'XXXX' ? addressChecksum(matches[1], matches[2]) : sanitizeHex(matches[3])
  };
}
/**
 * Compares two ADS account addresses.
 *
 * @param address1 ADS account address
 * @param address2 ADS account address
 * @returns {boolean}
 */

function compareAddresses(address1, address2) {
  var a1 = splitAddress(address1);
  var a2 = splitAddress(address2);
  return a1 && a2 && a1.nodeId === a2.nodeId && a1.userAccountId === a2.userAccountId;
}
/**
 * Compares two ADS account addresses (node number only).
 *
 * @param address1 ADS account address
 * @param address2 ADS account address
 * @returns {boolean}
 */

function compareAddressesByNode(address1, address2) {
  var a1 = splitAddress(address1);
  var a2 = splitAddress(address2);
  return a1 && a2 && a1.nodeId === a2.nodeId;
}
/**
 * Checks if ADS account address is valid.
 *
 * @param address ADS account address
 * @returns {boolean}
 */

function validateAddress(address) {
  if (!address) {
    return false;
  }

  var parts = splitAddress(address);

  if (!parts || !parts.nodeId || !parts.userAccountId || !parts.checksum) {
    return false;
  }

  return parts.checksum === addressChecksum(parts.nodeId, parts.userAccountId);
}
/**
 * Checks if ADS public or secret key is valid.
 *
 * @param key (64 hexadecimal characters)
 * @returns {boolean}
 */

function validateKey(key) {
  if (!key) {
    return false;
  }

  var keyRegexp = /^[0-9a-fA-F]{64}$/;
  return keyRegexp.test(key);
}
/**
 * Checks if ETH account address is valid.
 *
 * @param address e.g. 0001-00000001-8B4E
 * @returns {boolean}
 */

function validateEthAddress(address) {
  if (!address) {
    return false;
  }

  return /^(0x)?[0-9a-fA-F]{40}$/.test(address.trim());
}
function formatNumber(amount) {
  var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
  var trim = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  var decimal = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '.';
  var thousand = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : ',';
  return (Number(amount) || 0).toFixed(precision).replace(/([0-9]{2})(0+)$/, trim ? '$1' : '$1$2').replace(/\d(?=(\d{3})+\.)/g, "$&".concat(thousand)).replace('.', decimal);
}

function _classPrivateMethodInitSpec$1(obj, privateSet) { _checkPrivateRedeclaration$1(obj, privateSet); privateSet.add(obj); }

function _checkPrivateRedeclaration$1(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }

function _classPrivateMethodGet$1(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }

var _validateLength$1 = /*#__PURE__*/new WeakSet();

var TxEncoder = /*#__PURE__*/function () {
  function TxEncoder(obj) {
    _classCallCheck(this, TxEncoder);

    _classPrivateMethodInitSpec$1(this, _validateLength$1);

    _defineProperty(this, "pad", function (field, length) {
      return field.padStart(length, '0');
    });

    this.obj = obj;
    this.data = '';
    this.parsed = null;
    this.encode(TX_FIELDS.TYPE);
  }

  _createClass(TxEncoder, [{
    key: "encode",
    value: function encode(fieldName) {
      var data;
      var val = this.obj[fieldName];

      if (typeof val === 'undefined' || val === null) {
        throw new TransactionDataError("Undefined transaction filed ".concat(fieldName));
      }

      switch (fieldName) {
        case TX_FIELDS.ADDRESS:
        case TX_FIELDS.SENDER:
          {
            if (!validateAddress(val)) {
              throw new TransactionDataError("Invalid ADS address ".concat(val));
            }

            var address = splitAddress(val);
            var node = fixByteOrder(this.pad(address.nodeId, 4));
            var user = fixByteOrder(this.pad(address.userAccountId, 8));
            data = node + user;
            break;
          }

        case TX_FIELDS.AMOUNT:
          {
            val = new BigNumber(val).toString(16);

            _classPrivateMethodGet$1(this, _validateLength$1, _validateLength2$1).call(this, fieldName, val, 16);

            data = fixByteOrder(this.pad(val, 16));
            break;
          }

        case TX_FIELDS.MESSAGE_ID:
        case TX_FIELDS.NODE_MESSAGE_ID:
          {
            val = val.toString(16);

            _classPrivateMethodGet$1(this, _validateLength$1, _validateLength2$1).call(this, fieldName, val, 8);

            data = fixByteOrder(this.pad(val, 8));
            break;
          }

        case TX_FIELDS.MSG:
          {
            if (this.obj[TX_FIELDS.TYPE] === TX_TYPES.SEND_ONE) {
              _classPrivateMethodGet$1(this, _validateLength$1, _validateLength2$1).call(this, fieldName, val, 64);

              data = this.pad(val, 64);
            } else {
              var msg = val;
              var len = msg.length;

              if (len % 2 !== 0) {
                len += 1;
                msg = "0".concat(msg);
              }

              len /= 2;
              data = fixByteOrder(this.pad(len.toString(16), 4));
              data += msg;
            }

            break;
          }

        case TX_FIELDS.NODE_ID:
          {
            val = val.toString(16);

            _classPrivateMethodGet$1(this, _validateLength$1, _validateLength2$1).call(this, fieldName, val, 4);

            data = fixByteOrder(this.pad(val, 4));
            break;
          }

        case TX_FIELDS.PUBLIC_KEY:
          {
            if (!validateKey(val)) {
              throw new TransactionDataError("Invalid public key ".concat(val));
            }

            data = this.pad(val, 64);
            break;
          }

        case TX_FIELDS.TIME:
          {
            var date = _typeof(val) === 'object' ? val : new Date(val);
            var time = Math.floor(date.getTime() / 1000);
            data = fixByteOrder(this.pad(time.toString(16), 8));
            break;
          }

        case TX_FIELDS.TYPE:
          {
            var type = Object.keys(TX_TYPES_MAP).find(function (key) {
              return TX_TYPES_MAP[key] === val;
            });
            data = type ? this.pad(type, 2) : null;
            break;
          }

        default:
          throw new TransactionDataError("Invalid transaction field name ".concat(fieldName));
      }

      this.parsed = data;
      this.data += data;
      return this;
    }
  }, {
    key: "fill",
    value: function fill(charCount) {
      this.data += this.pad('', charCount);
      return this;
    }
  }, {
    key: "type",
    get: function get() {
      return this.obj[TX_FIELDS.TYPE];
    }
  }, {
    key: "lastEncodedField",
    get: function get() {
      return this.parsed;
    }
  }, {
    key: "encodedData",
    get: function get() {
      return this.data;
    }
  }]);

  return TxEncoder;
}();

function _validateLength2$1(fieldName, value, maxLength) {
  if (value.length > maxLength) {
    throw new TransactionDataError("Length of ".concat(fieldName, " cannot exceed ").concat(maxLength / 2, " bytes"));
  }
}

function _classPrivateMethodInitSpec(obj, privateSet) { _checkPrivateRedeclaration(obj, privateSet); privateSet.add(obj); }

function _checkPrivateRedeclaration(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }

function _classPrivateMethodGet(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }

var _validateLength = /*#__PURE__*/new WeakSet();

var TxDecoder = /*#__PURE__*/function () {
  function TxDecoder(data) {
    _classCallCheck(this, TxDecoder);

    _classPrivateMethodInitSpec(this, _validateLength);

    this.data = data;
    this.resp = {};
    this.parsed = null;
    this.decode(TX_FIELDS.TYPE);

    if (this.type !== TX_TYPES.GET_FIELDS) {
      this.decode(TX_FIELDS.SENDER);
    }
  }

  _createClass(TxDecoder, [{
    key: "decode",
    value: function decode(fieldName) {
      var parsed;

      switch (fieldName) {
        case TX_FIELDS.ADDRESS:
        case TX_FIELDS.SENDER:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 12);

            var node = fixByteOrder(this.data.substr(0, 4));
            var user = fixByteOrder(this.data.substr(4, 8));
            parsed = formatAddress(node, user);
            this.data = this.data.substr(12);
            break;
          }

        case TX_FIELDS.AMOUNT:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 16);

            parsed = fixByteOrder(this.data.substr(0, 16)); // parsed = formatMoney(parseInt(parsed, 16) / 100000000000, 11);
            // eslint-disable-next-line new-cap,no-undef

            parsed = new BigNumber("0x".concat(parsed));
            this.data = this.data.substr(16);
            break;
          }

        case TX_FIELDS.BLOCK_ID:
        case TX_FIELDS.BLOCK_ID_FROM:
        case TX_FIELDS.BLOCK_ID_TO:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 8);

            parsed = fixByteOrder(this.data.substr(0, 8)); // parsed = new Date(parseInt(parsed, 16) * 1000);

            this.data = this.data.substr(8);
            break;
          }

        case TX_FIELDS.TIME:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 8);

            var time = fixByteOrder(this.data.substr(0, 8));
            parsed = new Date(parseInt(time, 16) * 1000);
            this.data = this.data.substr(8);
            break;
          }

        case TX_FIELDS.MSG:
          {
            var expectedLength = this.resp[TX_FIELDS.TYPE] === TX_TYPES.SEND_ONE ? 64 : this.resp[TX_FIELDS.MSG_LEN] * 2;

            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, expectedLength, true);

            parsed = this.data;
            this.data = '';
            break;
          }

        case TX_FIELDS.MSG_LEN:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 4);

            parsed = fixByteOrder(this.data.substr(0, 4));
            parsed = parseInt(parsed, 16);
            this.data = this.data.substr(4);
            break;
          }

        case TX_FIELDS.MESSAGE_ID:
        case TX_FIELDS.NODE_MESSAGE_ID:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 8);

            parsed = fixByteOrder(this.data.substr(0, 8));
            parsed = parseInt(parsed, 16);
            this.data = this.data.substr(8);
            break;
          }

        case TX_FIELDS.NODE_ID:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 4);

            parsed = fixByteOrder(this.data.substr(0, 4));
            this.data = this.data.substr(4);
            break;
          }

        case TX_FIELDS.PUBLIC_KEY:
        case TX_FIELDS.VIP_HASH:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 64); // intentional lack of reverse - key and hash are not reversed


            parsed = this.data.substr(0, 64);
            this.data = this.data.substr(64);
            break;
          }

        case TX_FIELDS.STATUS_ACCOUNT:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 4);

            parsed = fixByteOrder(this.data.substr(0, 4));
            parsed = parseInt(parsed, 16);
            this.data = this.data.substr(4);
            break;
          }

        case TX_FIELDS.STATUS_NODE:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 8);

            parsed = fixByteOrder(this.data.substr(0, 8)); // node status has 32 bits
            // operation ' | 0' changes parsed type to int32

            /* eslint no-bitwise: ["error", { "int32Hint": true }] */

            parsed = parseInt(parsed, 16);
            this.data = this.data.substr(8);
            break;
          }

        case TX_FIELDS.TRANSACTION_ID:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 16);

            var _node = fixByteOrder(this.data.substr(0, 4));

            var msgId = fixByteOrder(this.data.substr(4, 8));
            var txOffset = fixByteOrder(this.data.substr(12, 4));
            parsed = "".concat(_node, ":").concat(msgId, ":").concat(txOffset);
            this.data = this.data.substr(16);
            break;
          }

        case TX_FIELDS.TYPE:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 2); // intentional lack of reverse - 1 byte does not need to be reversed


            var type = this.data.substr(0, 2);
            parsed = TX_TYPES_MAP[type];
            this.data = this.data.substr(2);
            break;
          }

        case TX_FIELDS.WIRE_COUNT:
          {
            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, 4);

            var count = fixByteOrder(this.data.substr(0, 4));
            parsed = parseInt(count, 16);
            this.data = this.data.substr(4);
            break;
          }

        case TX_FIELDS.WIRES:
          {
            var _count = this.resp[TX_FIELDS.WIRE_COUNT];
            var expLength = _count * 28; // 4+8+16(node+user+amount)

            _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, fieldName, expLength);

            parsed = [];

            for (var i = 0; i < _count; i += 1) {
              var _parsed$push;

              var _node2 = fixByteOrder(this.data.substr(0, 4));

              var _user = fixByteOrder(this.data.substr(4, 8));

              var amount = fixByteOrder(this.data.substr(12, 16));
              var address = formatAddress(_node2, _user);
              parsed.push((_parsed$push = {}, _defineProperty(_parsed$push, TX_FIELDS.ADDRESS, address), _defineProperty(_parsed$push, TX_FIELDS.AMOUNT, new BigNumber("0x".concat(amount))), _parsed$push));
              this.data = this.data.substr(28);
            }

            break;
          }

        default:
          throw new TransactionDataError('Invalid transaction field type');
      }

      this.resp[fieldName] = parsed;
      this.parsed = parsed;
      return this;
    }
  }, {
    key: "skip",
    value: function skip(charCount) {
      this.parsed = this.data.substr(0, charCount);
      this.data = this.data.substr(charCount);
      return this;
    }
  }, {
    key: "final",
    value: function final() {
      _classPrivateMethodGet(this, _validateLength, _validateLength2).call(this, 'extra', 0, true);
    }
  }, {
    key: "type",
    get: function get() {
      return this.resp[TX_FIELDS.TYPE];
    }
  }, {
    key: "sender",
    get: function get() {
      return this.resp[TX_FIELDS.SENDER];
    }
  }, {
    key: "lastDecodedField",
    get: function get() {
      return this.parsed;
    }
  }, {
    key: "decodedData",
    get: function get() {
      return this.resp;
    }
  }]);

  return TxDecoder;
}();

function _validateLength2(fieldName, expectedLength, strict) {
  if (this.data.length < expectedLength || strict && this.data.length > expectedLength) {
    throw new TransactionDataError("Invalid ".concat(fieldName, " data length; expected ").concat(expectedLength, " - got ").concat(this.data.length));
  }
}

/**
 * Encodes command data.
 *
 * @param command command data object
 * @returns {string}
 */

function encodeCommand(command) {
  var encoder = new TxEncoder(command);

  switch (encoder.type) {
    case TX_TYPES.BROADCAST:
      encoder.encode(TX_FIELDS.SENDER).encode(TX_FIELDS.MESSAGE_ID).encode(TX_FIELDS.TIME).encode(TX_FIELDS.MSG);
      break;

    case TX_TYPES.CHANGE_ACCOUNT_KEY:
      encoder.encode(TX_FIELDS.SENDER).encode(TX_FIELDS.MESSAGE_ID).encode(TX_FIELDS.TIME).encode(TX_FIELDS.PUBLIC_KEY);
      break;

    case TX_TYPES.CREATE_ACCOUNT:
      encoder.encode(TX_FIELDS.SENDER).encode(TX_FIELDS.MESSAGE_ID).encode(TX_FIELDS.TIME).encode(TX_FIELDS.NODE_ID).fill(8).encode(TX_FIELDS.PUBLIC_KEY);
      break;

    case TX_TYPES.SEND_ONE:
      encoder.encode(TX_FIELDS.SENDER).encode(TX_FIELDS.MESSAGE_ID).encode(TX_FIELDS.TIME).encode(TX_FIELDS.ADDRESS).encode(TX_FIELDS.AMOUNT).encode(TX_FIELDS.MSG);
      break;

    case TX_TYPES.CHANGE_NODE_KEY:
    case TX_TYPES.CREATE_NODE:
    case TX_TYPES.LOG_ACCOUNT:
    case TX_TYPES.GET_ACCOUNT:
    case TX_TYPES.GET_ACCOUNTS:
    case TX_TYPES.FIND_ACCOUNTS:
    case TX_TYPES.GET_BLOCK:
    case TX_TYPES.GET_BLOCKS:
    case TX_TYPES.GET_BROADCAST:
    case TX_TYPES.GET_FIELDS:
    case TX_TYPES.GET_LOG:
    case TX_TYPES.GET_MESSAGE:
    case TX_TYPES.GET_MESSAGE_LIST:
    case TX_TYPES.GET_SIGNATURES:
    case TX_TYPES.GET_TRANSACTION:
    case TX_TYPES.GET_VIPKEYS:
    case TX_TYPES.RETRIEVE_FUNDS:
    case TX_TYPES.SEND_AGAIN:
    case TX_TYPES.SEND_MANY:
    case TX_TYPES.SET_ACCOUNT_STATUS:
    case TX_TYPES.SET_NODE_STATUS:
    case TX_TYPES.UNSET_ACCOUNT_STATUS:
    case TX_TYPES.UNSET_NODE_STATUS:
    case TX_TYPES.GET_GATEWAYS:
    case TX_TYPES.GET_GATEWAY_FEE:
    case TX_TYPES.GET_TIMESTAMP:
      throw new TransactionDataError("Unsupported type of transaction ".concat(command[TX_FIELDS.TYPE]));

    default:
      throw new TransactionDataError("Unknown type of transaction ".concat(command[TX_FIELDS.TYPE]));
  }

  return encoder.encodedData.toUpperCase();
}
/**
 * Decodes command data.
 *
 * @param data string encoded command (hexadecimal characters)
 * @returns {object}
 */

function decodeCommand(data) {
  var decoder = new TxDecoder(data);

  switch (decoder.type) {
    case TX_TYPES.BROADCAST:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME).decode(TX_FIELDS.MSG_LEN).decode(TX_FIELDS.MSG);
      break;

    case TX_TYPES.CHANGE_ACCOUNT_KEY:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME).decode(TX_FIELDS.PUBLIC_KEY);
      break;

    case TX_TYPES.CHANGE_NODE_KEY:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME).decode(TX_FIELDS.NODE_ID).decode(TX_FIELDS.PUBLIC_KEY);
      break;

    case TX_TYPES.CREATE_ACCOUNT:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME).decode(TX_FIELDS.NODE_ID).skip(8).decode(TX_FIELDS.PUBLIC_KEY);
      break;

    case TX_TYPES.CREATE_NODE:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME);
      break;

    case TX_TYPES.GET_ACCOUNT:
      decoder.decode(TX_FIELDS.ADDRESS).decode(TX_FIELDS.TIME);
      break;

    case TX_TYPES.GET_ACCOUNTS:
      decoder.decode(TX_FIELDS.TIME).decode(TX_FIELDS.BLOCK_ID) // previous block id
      .decode(TX_FIELDS.NODE_ID);
      break;

    case TX_TYPES.GET_BLOCK:
      decoder.decode(TX_FIELDS.BLOCK_ID) // previous block id
      .decode(TX_FIELDS.TIME);
      break;

    case TX_TYPES.GET_BLOCKS:
      decoder.decode(TX_FIELDS.TIME).decode(TX_FIELDS.BLOCK_ID_FROM).decode(TX_FIELDS.BLOCK_ID_TO);
      break;

    case TX_TYPES.GET_BROADCAST:
      decoder.decode(TX_FIELDS.BLOCK_ID).decode(TX_FIELDS.TIME);
      break;

    case TX_TYPES.GET_LOG:
      decoder.decode(TX_FIELDS.TIME);
      break;

    case TX_TYPES.GET_MESSAGE:
      decoder.decode(TX_FIELDS.TIME).decode(TX_FIELDS.BLOCK_ID).decode(TX_FIELDS.NODE_ID).decode(TX_FIELDS.NODE_MESSAGE_ID);
      break;

    case TX_TYPES.GET_MESSAGE_LIST:
      decoder.decode(TX_FIELDS.TIME).decode(TX_FIELDS.BLOCK_ID);
      break;

    case TX_TYPES.GET_SIGNATURES:
      decoder.decode(TX_FIELDS.TIME).decode(TX_FIELDS.BLOCK_ID);
      break;

    case TX_TYPES.GET_TRANSACTION:
      decoder.decode(TX_FIELDS.TIME).decode(TX_FIELDS.TRANSACTION_ID);
      break;

    case TX_TYPES.GET_VIPKEYS:
      decoder.decode(TX_FIELDS.TIME).decode(TX_FIELDS.VIP_HASH);
      break;

    case TX_TYPES.LOG_ACCOUNT:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME);
      break;

    case TX_TYPES.RETRIEVE_FUNDS:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME).decode(TX_FIELDS.ADDRESS);
      break;

    case TX_TYPES.SEND_MANY:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME).decode(TX_FIELDS.WIRE_COUNT).decode(TX_FIELDS.WIRES);
      break;

    case TX_TYPES.SEND_ONE:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME).decode(TX_FIELDS.ADDRESS).decode(TX_FIELDS.AMOUNT).decode(TX_FIELDS.MSG);
      break;

    case TX_TYPES.SET_ACCOUNT_STATUS:
    case TX_TYPES.UNSET_ACCOUNT_STATUS:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME).decode(TX_FIELDS.ADDRESS).decode(TX_FIELDS.STATUS_ACCOUNT);
      break;

    case TX_TYPES.SET_NODE_STATUS:
    case TX_TYPES.UNSET_NODE_STATUS:
      decoder.decode(TX_FIELDS.MESSAGE_ID).decode(TX_FIELDS.TIME).decode(TX_FIELDS.NODE_ID).decode(TX_FIELDS.STATUS_NODE);
      break;

    case TX_TYPES.GET_FIELDS:
      // function `get_fields` does not return `tx.data`
      throw new TransactionDataError('Transaction is not parsable');

    default:
      throw new TransactionDataError('Unknown type of transaction');
  }

  decoder["final"]();
  return decoder.decodedData;
}
function decodeType(data) {
  return new TxDecoder(data).type;
}
function decodeSender(data) {
  return new TxDecoder(data).sender;
}
/**
 * Decode hex message.
 *
 * @param value message in hex
 * @param onlyPrintable decode only if printable
 * @returns {string}
 */

function decodeMessage(value) {
  var onlyPrintable = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  var hex = value === null || typeof value === 'undefined' ? '0' : value;
  hex = hex.toString().trim().replace(/^0x/, ''); // force conversion

  var str = '';

  for (var i = 0; i < hex.length; i += 2) {
    var chars = hex.substr(i, 2);
    var code = parseInt(chars, 16);

    if (code !== 0) {
      if (onlyPrintable && (code < 32 || code >= 127)) {
        str = '--- non-printable ---';
        break;
      }

      str += String.fromCharCode(code);
    }
  }

  return str.length > 0 ? str : '--- empty ---';
}

var ED25519_CURVE = 'ed25519 seed';
var HARDENED_OFFSET = 0x80000000;
var pathRegex = /^m(\/[0-9]+')+$/;

function replaceDerive(val) {
  return val.replace('\'', '');
}

function isValidPath(path) {
  if (!pathRegex.test(path)) {
    return false;
  }

  return !path.split('/').slice(1).map(replaceDerive).some(isNaN);
}

function ckdPriv(_ref, index) {
  var key = _ref.key,
      chainCode = _ref.chainCode;
  var hash = CryptoJS.HmacSHA512(CryptoJS.enc.Hex.parse("00".concat(key).concat(index.toString(16))), CryptoJS.enc.Hex.parse(chainCode)).toString(CryptoJS.enc.Hex);
  return {
    key: hash.slice(0, 64),
    chainCode: hash.slice(64)
  };
}
/**
 * Returns secret key derived from seed phrase.
 *
 * @param seed seed phrase
 * @returns {string} secret key (64 hexadecimal characters)
 */


function getSecretKey(seed) {
  return CryptoJS.SHA256(seed).toString(CryptoJS.enc.Hex);
}
/**
 * Returns public key derived from secret key.
 *
 * @param secretKey secret key (64 hexadecimal characters)
 * @returns {string} public key (64 hexadecimal characters)
 */

function getPublicKey(secretKey) {
  var _NaCl$sign$keyPair$fr = NaCl.sign.keyPair.fromSeed(hexToByte(secretKey)),
      publicKey = _NaCl$sign$keyPair$fr.publicKey;

  return byteToHex(publicKey);
}
/**
 * Returns master key derived from seed.
 *
 * @param seed private key seed (hexadecimal characters)
 * @returns {{chainCode: string, key: string}}
 */

function getMasterKey(seed) {
  var hash = CryptoJS.HmacSHA512(CryptoJS.enc.Hex.parse(seed), ED25519_CURVE).toString(CryptoJS.enc.Hex);
  return {
    key: hash.slice(0, 64),
    chainCode: hash.slice(64)
  };
}
/**
 * Returns next derived key from seed.
 *
 * @param path derive path
 * @param seed private key seed (hexadecimal characters)
 * @returns {{chainCode: string, key: string}}
 */

function getNextKey(path, seed) {
  if (!isValidPath(path)) {
    throw new Error('Invalid derivation path');
  }

  var _getMasterKey = getMasterKey(seed),
      key = _getMasterKey.key,
      chainCode = _getMasterKey.chainCode;

  var segments = path.split('/').slice(1).map(replaceDerive).map(function (el) {
    return parseInt(el, 10);
  });
  return segments.reduce(function (parentKeys, segment) {
    return ckdPriv(parentKeys, segment + HARDENED_OFFSET);
  }, {
    key: key,
    chainCode: chainCode
  });
}
/**
 * Signs data with a secret key.
 *
 * @param secretKey secret key 32 bytes
 * @param data data (hexadecimal characters); in case of transaction: `tx.account_hashin` + `tx.data`
 * @returns {string} signature 64 bytes
 */

function sign(secretKey, data) {
  return byteToHex(NaCl.sign.detached(hexToByte(data), hexToByte(secretKey + getPublicKey(secretKey))));
}
/**
 * Signs text with a secret key.
 *
 * @param secretKey secret key 32 bytes
 * @param text data text
 * @returns {string} signature 64 bytes
 */

function signText(secretKey, text) {
  return sign(secretKey, stringToHex(text));
}
/**
 * Validates signature.
 *
 * @param signature (128 hexadecimal characters)
 * @param publicKey (64 hexadecimal characters)
 * @param data data (hexadecimal characters)
 * @returns {boolean}
 */

function validateSignature(signature, publicKey, data) {
  return NaCl.sign.detached.verify(hexToByte(data), hexToByte(signature), hexToByte(publicKey));
}

var BLOCK_LENGTH = 512;
var DIVIDEND_LENGTH = 2048;
var TOTAL_SUPPLY = new BigNumber('3875820600000000000');
var DERIVATION_PATH = 'm/44\'/311\'/';
var TX_MIN_FEE = 10000;
var TX_BROADCAST_FEE = 1000;
var TX_CHANGE_KEY_FEE = 10000000;
var TX_LOCAL_TRANSFER_FEE = 0.0005;
var TX_REMOTE_TRANSFER_FEE = 0.0005;
/**
 * Formats ADS amount
 *
 * @param amount
 * @param precision
 * @param trim
 * @param decimal
 * @param thousand
 * @returns {*}
 */

function formatAdsMoney(amount) {
  var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 4;
  var trim = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var decimal = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '.';
  var thousand = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : ',';
  return formatNumber(amount, precision, trim, decimal, thousand);
}
/**
 * Formats ADS amount in clicks
 *
 * @param value
 * @param precision
 * @param trim
 * @param decimal
 * @param thousand
 * @returns {string}
 */

function formatClickMoney(value) {
  var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 11;
  var trim = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var decimal = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '.';
  var thousand = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : ',';
  var p = Math.max(precision, 2);
  var v = value;
  v = "".concat(v || '0').padStart(11, '0');
  var l = v.length - 11;
  var a = v.substr(0, l) || '0';
  var j = a.length > 3 ? a.length % 3 : 0;
  var b = Math.round(parseInt("".concat(v, "0").substr(l, p + 1), 10) / 10).toString().padStart(p, '0');

  if (trim) {
    b = b.replace(/([0-9]{2})0+$/, '$1');
  }

  return (j ? a.substr(0, j) + thousand : '') + a.substr(j).replace(/(\d{3})(?=\d)/g, "$1".concat(thousand)) + decimal + b;
}
/**
 * Converts ADS amount in clicks from string to number
 *
 * @param value
 * @returns {BigNumber|null}
 */

function strToClicks(value) {
  var matches = value.match(/^([0-9]*)[.,]?([0-9]{0,11})[0-9]*$/);
  return matches ? new BigNumber(matches[1] + matches[2].padEnd(11, '0')) : null;
}
/**
 * Calculates transaction fee.
 *
 * @param command command data object
 * @returns {number}
 */

function calculateFee(command) {
  var encoder = new TxEncoder(command);
  var length;
  var fee = 0;

  switch (command[TX_FIELDS.TYPE]) {
    case TX_TYPES.BROADCAST:
      length = encoder.encode(TX_FIELDS.MSG).lastEncodedField.length / 2 - 2;
      fee = TX_MIN_FEE;

      if (length > 32) {
        fee += (length - 32) * TX_BROADCAST_FEE;
      }

      break;

    case TX_TYPES.CHANGE_ACCOUNT_KEY:
      fee = TX_CHANGE_KEY_FEE;
      break;

    case TX_TYPES.SEND_ONE:
      fee = command[TX_FIELDS.AMOUNT] * TX_LOCAL_TRANSFER_FEE;

      if (!compareAddressesByNode(command[TX_FIELDS.SENDER], command[TX_FIELDS.ADDRESS])) {
        fee += command[TX_FIELDS.AMOUNT] * TX_REMOTE_TRANSFER_FEE;
      }

      fee = Math.floor(fee);
      break;
  }

  return Math.max(TX_MIN_FEE, fee);
}
/**
 * Calculates transaction total amount.
 *
 * @param command command data object
 * @returns {number}
 */

function calculateChargedAmount(command) {
  return calculateFee(command) + parseInt(command[TX_FIELDS.AMOUNT], 10);
}
/**
 * Calculates transaction received amount.
 *
 * @param externalFee external fee in clicks
 * @param command command data object
 * @returns {number}
 */

function calculateReceivedAmount(externalFee, command) {
  return Math.max(0, parseInt(command[TX_FIELDS.AMOUNT], 10) - parseInt(externalFee, 10));
}

export { ABI, BLOCK_LENGTH, DERIVATION_PATH, DIVIDEND_LENGTH, TOTAL_SUPPLY, TX_BROADCAST_FEE, TX_CHANGE_KEY_FEE, TX_FIELDS, TX_LOCAL_TRANSFER_FEE, TX_MIN_FEE, TX_REMOTE_TRANSFER_FEE, TX_TYPES, addressChecksum, calculateChargedAmount, calculateFee, calculateReceivedAmount, compareAddresses, compareAddressesByNode, decodeCommand, decodeMessage, decodeSender, decodeType, encodeCommand, formatAddress, formatAdsMoney, formatClickMoney, formatNumber, getMasterKey, getNextKey, getPublicKey, getSecretKey, sign, signText, splitAddress, strToClicks, validateAddress, validateEthAddress, validateKey, validateSignature };
